<template>
  <div class="mapWrapper" :style="mapWrapperStyle">
    <div
      ref="MapCanvas"
      id="MapCanvas"
      class="MapCanvas"
      style="width: 100%; height: 100%"
    ></div>
  </div>
</template>
<script>
/* global google */
import { loadGoogleMaps } from "@/mixins/LoadGoogleMaps";
import { systemMessages } from "@/mixins/SystemMessages";
import { toRaw } from "vue";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Debounce } from "@/mixins/Debounce";

export default {
  name: "ServicesDirectoryMap",
  mixins: [systemMessages, Debounce],
  props: {
    resizeMapOnDataPanelChange: {
      type: Boolean,
      default: false,
    },
    containerHeight: {
      type: Number,
      default: 0,
    },
    dataPanelHeight: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    map: null,
    mapCentre: null,
    mapZoom: null,
    polygonObjects: [],
    fetchingCustomAreaGeometries: false,
    abortControllers: [],
    markers: [],
    infoWindows: {},
    markerCluster: null,
    areServicesClustered: true,
    debounceMapResize: null,
  }),
  computed: {
    /**
     * calculate and set the height of the map canvas based on the parent container
     * and data panel state (e.g. collapsed, normal, fullscreen)
     */
    mapWrapperStyle: {
      get() {
        const wrapperSize = this.containerHeight - this.dataPanelHeight;
        if (this.resizeMapOnDataPanelChange && wrapperSize > 0) {
          return `height: ${wrapperSize}px; transition: height 0.3s ease;`; //full height minus height of data panel
        } else {
          return `height: calc(100% - 60px)`; // full height minus data panel collapsed state
        }
      },
    },
    satelliteView: {
      get() {
        return this.$store.getters["servicesDirectory/satelliteView"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSatelliteView", val);
      },
    },
    selectedCustomAreas: {
      get() {
        return this.$store.getters["servicesDirectory/selectedCustomAreas"];
      },
      set(val) {
        // the value is synced with URL as well as with vuex
        this.$router.push({
          query: {
            ...this.$route.query,
            selectedCustomAreas: JSON.stringify(val),
          },
        });
        this.$store.commit("servicesDirectory/setSelectedCustomAreas", val);
      },
    },
    loading() {
      return (
        this.$store.getters["servicesDirectory/fetchingServices"] ||
        this.$store.getters["servicesDirectory/fetchingCustomAreas"] ||
        this.fetchingCustomAreaGeometries
      );
    },
  },
  mounted() {
    this.initializeMap();
    this.debounceMapResize = this.debounce(this.resizeMapCanvas, 50);

    this.satelliteView = this.$route.query?.satellite === "true";

    // Set up listener for clicking on street view button
    this.$nextTick(() => {
      document.addEventListener("click", (event) => {
        // Make sure it's a street view button that's been pressed
        const isStreetViewButton = event.target.id === "streetViewButton";
        if (!isStreetViewButton) {
          return;
        }

        this.openStreetView(
          event.target.getAttribute("lat"),
          event.target.getAttribute("lng"),
        );
      });
    });
  },
  methods: {
    getRandomOffset() {
      // Generate a random offset between -0.0005 and 0.0005
      return (Math.random() - 0.5) * 0.001;
    },
    clearMarkers() {
      // pull the real marker out of the reactive proxy
      this.markers.forEach((proxyMarker) => {
        const m = toRaw(proxyMarker);
        m.map = null;
      });

      this.markers = [];

      // Clear the clusterer
      if (this.markerCluster) {
        this.markerCluster.clearMarkers();
      }
    },
    async renderMapMarkers() {
      await this.getServicesGroupsAndMetaHierarchy();
      const rawMap = toRaw(this.map);

      // Clear old markers
      this.clearMarkers();

      if (!rawMap) {
        return;
      }

      if (
        !this.$store.state.servicesDirectory.SDfetchedServices ||
        !this.$store.state.servicesDirectory.SDfetchedServices.length
      ) {
        return;
      }

      if (
        !this.$store.state.servicesDirectory.SDbuiltPNGIcons ||
        Object.keys(this.$store.state.servicesDirectory.SDbuiltPNGIcons)
          .length === 0
      ) {
        return;
      }

      // get the libraries we need
      const { AdvancedMarkerElement } =
        await google.maps.importLibrary("marker");
      const { InfoWindow } = await google.maps.importLibrary("maps");

      this.markers = [];
      const processedPostcodes = [];

      // If no custom areas are selected, set the map to the initial zoom and centre
      if (!this.selectedCustomAreas?.length) {
        toRaw(this.map).setCenter({
          lat: this.$store.state.config.siteConfig.initialMapZoom.lat,
          lng: this.$store.state.config.siteConfig.initialMapZoom.lng,
        });
        toRaw(this.map).setZoom(
          this.$store.state.config.siteConfig.initialMapZoom.zoom,
        );
      }

      // Process services
      for (const service of this.$store.state.servicesDirectory
        .SDfetchedServices) {
        if (!service.plot_on_map) {
          continue;
        }

        let lat = parseFloat(service.lat);
        let lng = parseFloat(service.lng);
        if (isNaN(lat) || isNaN(lng)) {
          continue;
        }

        // Check if we've already processed this postcode
        if (processedPostcodes.includes(service.postcode)) {
          // If we have, offset the coordinates
          lat += this.getRandomOffset();
          lng += this.getRandomOffset();
        } else {
          // If we've not processed this one before, keep track of it
          processedPostcodes.push(service.postcode);
        }

        try {
          // Use the built PNG icon for this service type
          const markerIcon =
            this.$store.state.servicesDirectory.SDbuiltPNGIcons[
              service.type_id
            ];
          if (!markerIcon) {
            continue;
          }

          // Create an HTMLImageElement for the marker content
          let imgElement = document.createElement("img");
          imgElement.src = markerIcon;
          imgElement.width = 38;
          imgElement.height = 38;
          imgElement.id = service.id;
          imgElement.style.filter = `
          drop-shadow(2px 2px 0 white)
          drop-shadow(-2px 2px 0 white)
          drop-shadow(2px -2px 0 white)
          drop-shadow(-2px -2px 0 white)
        `;
          imgElement.setAttribute("map-marker", "true");

          const advancedMarker = new AdvancedMarkerElement({
            position: { lat, lng },
            content: imgElement,
            title: "View details",
          });

          advancedMarker.id = service.id;
          advancedMarker.type_id = service.type_id;
          advancedMarker.addListener(
            "click",
            () => {
              if (!this.infoWindows[service.id]) {
                this.infoWindows[service.id] = new InfoWindow({
                  content: "",
                  maxWidth: 360,
                  padding: 0,
                });
              }

              const infoWindow = this.infoWindows[service.id];

              if (infoWindow.isOpen) {
                infoWindow.close(rawMap, advancedMarker);
              } else {
                infoWindow.setContent(this.getInfoContent(service));
                infoWindow.setPosition(advancedMarker.position);
                infoWindow.open(rawMap, advancedMarker);
              }
            },
            { passive: true },
          );
          this.markers.push(advancedMarker);
        } catch (error) {
          console.error("Error creating marker for service:", service, error);
        }
      }

      // Add markers to the map with clustering
      if (this.areServicesClustered) {
        this.markerCluster = new MarkerClusterer({
          markers: this.markers,
          map: rawMap,
        });

        this.markerCluster.addListener("clusteringbegin", () => {
          // You can add loading state here if needed
        });

        this.markerCluster.addListener("clusteringend", () => {
          // You can remove loading state here if needed
        });
      } else {
        // If clustering is disabled, add markers directly to the map
        this.markers.forEach((marker) => marker.setMap(rawMap));
      }
    },
    async initializeMap() {
      const center = { lat: 54.36, lng: -2.59 };
      const zoom = 6;

      try {
        loadGoogleMaps(center, zoom, "MapCanvas", {
          showUI: false,
        }).then((loadedMap) => {
          this.map = loadedMap;
          const rawMap = toRaw(this.map);
          rawMap.setMapTypeId(this.satelliteView ? "hybrid" : "roadmap");

          //keep track of center and zoom for resizing
          rawMap.addListener("idle", () => {
            this.mapCentre = rawMap.getCenter();
            this.mapZoom = rawMap.getZoom();
          });
        });
      } catch (error) {
        this.errorMsg({ title: "Failed to load Google Maps" });
      }
    },
    async fetchCustomAreaGeometries() {
      if (!this.selectedCustomAreas?.length) {
        return this.clearMap();
      }
      this.fetchingCustomAreaGeometries = true;

      // create and store controller for this fetch
      const controller = new AbortController();
      this.abortControllers.push(controller);
      const signal = controller.signal;

      try {
        const { data } = await this.$axios.post(
          "custom-areas-polygon",
          this.selectedCustomAreas,
          { signal },
        );
        await this.clearMap();
        await this.uploadGeometriesOntoMap(data.features);
      } catch (error) {
        // ignore aborts, show other errors
        if (error.name !== "CanceledError") {
          this.errorPill({ title: "Failed to fetch geometries" });
        }
      } finally {
        this.fetchingCustomAreaGeometries = false;
        // remove this controller from pending list
        this.abortControllers = this.abortControllers.filter(
          (c) => c !== controller,
        );
      }
    },
    async clearMap() {
      return new Promise((resolve) => {
        this.polygonObjects.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.polygonObjects = [];
        resolve();
      });
    },
    async uploadGeometriesOntoMap(feature) {
      if (feature?.length) {
        for (const f of feature) {
          const geometry = f.geometry;
          const geometryType = geometry.type;
          const areaId = f.properties.custom_area_id;
          const color = this.satelliteView ? "#FF0000" : "#000000";
          const { Polygon } = await google.maps.importLibrary("maps");

          if (geometryType === "Polygon") {
            // Polygon with possible holes
            const googleMapsPaths = geometry.coordinates.map((ring) =>
              ring.map((coord) => {
                return { lat: coord[1], lng: coord[0] };
              }),
            );

            this.polygonObjects.push(
              new Polygon({
                map: toRaw(this.map),
                paths: googleMapsPaths,
                fillOpacity: 0,
                strokeWeight: 3,
                strokeColor: color,
                zIndex: 10,
                clickable: false,
                custom_area_id: areaId,
              }),
            );
          } else if (geometryType === "MultiPolygon") {
            // MultiPolygon
            const multiCoordinates = geometry.coordinates;
            multiCoordinates.forEach((coords) => {
              const googleMapsPaths = coords.map((coord) => {
                return coord.map((subCoord) => {
                  return { lat: subCoord[1], lng: subCoord[0] };
                });
              });

              this.polygonObjects.push(
                new Polygon({
                  map: toRaw(this.map),
                  paths: googleMapsPaths,
                  fillOpacity: 0,
                  strokeWeight: 3,
                  strokeColor: color,
                  zIndex: 10,
                  clickable: false,
                  custom_area_id: areaId,
                }),
              );
            });
          }
        }
        this.zoomToCustomAreas();
      }
    },
    //fits map bounds to all selected custom areas
    zoomToCustomAreas() {
      const bounds = this.getSelectedAreaBounds();
      toRaw(this.map).fitBounds(bounds);
    },
    //zoom map to given services bounds
    zoomToServices(bounds) {
      const map = toRaw(this.map);

      map.fitBounds(bounds);

      //prevent map zooming too close
      const maxZoom = 18; // adjust as needed

      // Enforce max zoom after bounds settle
      google.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > maxZoom) {
          map.setZoom(maxZoom);
        }
      });
    },
    /**
     * reset the canvas size based on container size, re-centre
     * and zoom.  If there are selected areas, fetch the bounds
     * and size the map to those
     */
    resizeMapCanvas() {
      this.$nextTick(() => {
        const rawMap = toRaw(this.map);

        if (this.polygonObjects.length > 0 && rawMap) {
          google.maps.event.trigger(rawMap, "resize");

          const bounds = this.getSelectedAreaBounds();

          if (!bounds || typeof bounds.extend !== "function") {
            console.warn("Invalid map bounds returned");
            return;
          }

          // Wait 100ms after resize to ensure canvas has settled beforefitting bounds
          setTimeout(() => {
            rawMap.fitBounds(bounds);
          }, 100);
        }
      });
    },
    /**
     * return a bounding box for any selected areas on
     * the map
     */
    getSelectedAreaBounds() {
      const bounds = new google.maps.LatLngBounds();
      this.polygonObjects.forEach((polygon) => {
        polygon.getPath().forEach((latLng) => {
          bounds.extend(latLng);
        });
      });

      return bounds;
    },
    changeColourForCustomAreas(color) {
      this.polygonObjects.forEach((polygon) => {
        polygon.setOptions({
          strokeColor: color,
        });
      });
    },
    getInfoContent(service) {
      let labels = {};

      // Parse labels if it's a JSON string
      if (service.labels) {
        try {
          labels =
            typeof service.labels === "string"
              ? JSON.parse(service.labels)
              : service.labels;
        } catch (error) {
          console.error("Error parsing labels:", error);
          labels = {};
        }
      }

      // Get the icon data for this service type
      const iconData =
        this.$store.state.servicesDirectory.SDmetadataIcons[service.type_id];
      const backgroundColor = iconData ? iconData.colour : "#1976d2";
      const svgPath = iconData ? iconData.svg_path : "";

      // Calculate contrast color for the icon
      const iconColor = this.getContrastColor(backgroundColor);

      let content = `<div style="width: 360px; max-height: inherit; overflow: hidden; display: flex; border-radius: 8px; transition: all 0.3s ease-out;">
                       <div style="background-color: ${backgroundColor}; width: 17%; display: flex; justify-content: center; align-items: center;">`;

      if (svgPath) {
        content += `<svg width="32" height="32" viewBox="0 0 24 24" style="fill: ${iconColor};">
                     <path d="${svgPath}"/>
                   </svg>`;
      } else {
        content += `<i class="mdi-map-marker mdi v-icon notranslate v-theme--light v-icon--size-default" aria-hidden="true" style="font-size: 32px; color: ${iconColor};"></i>`;
      }

      content += `</div>
                   <div style="background-color: white; width: 83%; overflow-y: auto; padding: 0 10px 10px 10px; text-align: left">`;

      // Add the service name as a header
      content += `<div class="d-flex py-3" style="width: 85%"><div class="text-body-1 font-weight-medium">${service.service_name}</div><div style="flex-grow: 1;"></div></div>`;

      // Run through any labels and add them all in, aligned to the left
      for (const key in labels) {
        if (Object.prototype.hasOwnProperty.call(labels, key)) {
          let labelValue = labels[key].trim();

          if (labelValue) {
            // Check if the labelValue is a URL and convert it to a link
            if (this.isURL(labelValue)) {
              labelValue = `<a href="${labelValue}" target="_blank">${labelValue}</a>`;
            }

            content += `<div class="pb-2 text-body-2"><div class="font-weight-regular" style="color: #7F7F7F">${key}</div> <div class="text font-weight-medium">${labelValue}</div></div>`;
          }
        }
      }

      // Add the streetview button and add closing tags
      content += `<div class="d-flex justify-center">
                    <button
                      id="streetViewButton"
                      lat="${service.lat}"
                      lng="${service.lng}"
                      class="v-btn v-theme--light v-btn--density-default v-btn--size-default v-btn--variant-outlined w-100 mt-4"
                      style="color: #1976d2; border-radius: 0; padding: 7px;"
                    >
                      OPEN STREET VIEW
                    </button>
                  </div>
                  </div></div>
                 `;

      const element = document.createElement("div");
      element.classList.add("service-popup");
      element.innerHTML = content;
      element.style.maxHeight = "inherit";

      return element;
    },
    isURL(str) {
      const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      return urlRegex.test(str);
    },
    getContrastColor(backgroundColor) {
      // Convert hex to RGB
      const hex = backgroundColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Calculate luminance using the relative luminance formula
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return black for light backgrounds, white for dark backgrounds
      return luminance > 0.5 ? "#000000" : "#ffffff";
    },
    openStreetView(lat, lng) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);

      const streetViewService = new google.maps.StreetViewService();
      streetViewService.getPanorama(
        { location: { lat: latNum, lng: lngNum }, radius: 50 },
        (data, status) => {
          if (status === google.maps.StreetViewStatus.OK) {
            const panorama = new google.maps.StreetViewPanorama(
              this.map.getDiv(),
              {
                pano: data.location.pano,
                pov: { heading: 34, pitch: 10 },
                enableCloseButton: true,
              },
            );
            this.map.setStreetView(panorama);
            this.map.controls[google.maps.ControlPosition.TOP_LEFT].push();
          } else {
            this.warnMsg({
              title: "No Street View",
              message: "No Street View data available for this location.",
            });
          }
        },
      );
    },
    async getServicesGroupsAndMetaHierarchy() {
      // Only make AJAX call if data is null
      if (!this.$store.state.servicesDirectory.servicesGroupsAndMetaHierarchy) {
        this.$axios
          .get("/sd-services-metadata-groups/hierarchy")
          .then((response) => {
            this.$store.commit(
              "servicesDirectory/setServicesGroupsAndMetaHierarchy",
              response.data,
            );
          })
          .catch((error) => {
            console.error("Error fetching services:", error);
          });
      }
    },
    zoomToLocation(locationData) {
      if (!toRaw(this.map)) return;

      const rawMap = toRaw(this.map);

      if (locationData.bounds) {
        // If we have bounds, fit the map to those bounds
        rawMap.fitBounds(locationData.bounds);
      } else {
        // Otherwise, center on the specific coordinates
        rawMap.setCenter({
          lat: locationData.lat,
          lng: locationData.lng,
        });
        rawMap.setZoom(15); // Set a reasonable zoom level for a location
      }
    },
  },
  watch: {
    // Watch the loading computed property and emit systemBusy events
    loading: {
      handler(isLoading) {
        this.emit.emit("systemBusy", {
          busy: isLoading,
          showProgressLoader: isLoading,
          showAntiMasher: false,
        });
      },
    },
    satelliteView: {
      handler(isSatelliteView) {
        if (toRaw(this.map)) {
          toRaw(this.map).setMapTypeId(isSatelliteView ? "hybrid" : "roadmap");

          // Change the color of custom areas based on the view
          const color = isSatelliteView ? "#FF0000" : "#000000";
          this.changeColourForCustomAreas(color);
        }
      },
    },
    selectedCustomAreas: {
      handler(newSelectedAreas) {
        // cancel all in-flight requests then fetch new geometries
        if (this.abortControllers.length) {
          this.abortControllers.forEach((c) => c.abort());
          this.abortControllers = [];
        }
        this.fetchCustomAreaGeometries();

        // If no custom areas are selected, reset map to initial zoom and center
        if (!newSelectedAreas?.length && toRaw(this.map)) {
          toRaw(this.map).setCenter({
            lat: this.$store.state.config.siteConfig.initialMapZoom.lat,
            lng: this.$store.state.config.siteConfig.initialMapZoom.lng,
          });
          toRaw(this.map).setZoom(
            this.$store.state.config.siteConfig.initialMapZoom.zoom,
          );
        }
      },
    },
    dataPanelHeight: {
      handler() {
        if (this.resizeMapOnDataPanelChange) {
          this.debounceMapResize();
        }
      },
    },
  },
};
</script>
<style scoped></style>
